import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import Decimal from 'decimal.js';
import { DiscountModel } from '~/models/DiscountModel';
import { LineItemType } from '~/models/invoice-draft/model';
import * as timeModel from '~/models/time';
import { currencyUtils } from '~/utils/currency.utils';
import { AvailableCreditModel } from '../AvailableCreditModel';
import { centsToDecimal, centsTotals } from '../invoice-summary/translator';
import { matchLineItem } from './selectors';
export function fromDTO(invoiceDraftDTO) {
    var _invoiceDraftDTO_attachments, _invoiceDraftDTO_appliedTax;
    const { issueDate, paymentDate, totalAmount } = invoiceDraftDTO;
    const credits = invoiceDraftDTO.credits.map((c)=>new AvailableCreditModel(c));
    const lineItems = invoiceDraftDTO.lineItems.map(lineItemFromDTO);
    return {
        attachments: (_invoiceDraftDTO_attachments = invoiceDraftDTO.attachments) !== null && _invoiceDraftDTO_attachments !== void 0 ? _invoiceDraftDTO_attachments : [],
        issueDate: timeModel.fromDateStampObject(issueDate),
        paymentDate: timeModel.fromDateStampObject(paymentDate),
        totalAmount: new Decimal(totalAmount).div(100).toDP(),
        totals: invoiceDraftDTO.totals ? centsTotals(invoiceDraftDTO.totals) : null,
        appliedTax: (_invoiceDraftDTO_appliedTax = invoiceDraftDTO.appliedTax) !== null && _invoiceDraftDTO_appliedTax !== void 0 ? _invoiceDraftDTO_appliedTax : null,
        credits,
        lineItems
    };
}
const { dollarsToCents } = currencyUtils;
function totalsToDTO(totals) {
    return {
        subtotal: dollarsToCents(totals.subtotal),
        tax: dollarsToCents(totals.tax),
        withoutCredits: dollarsToCents(totals.withoutCredits),
        credits: dollarsToCents(totals.credits)
    };
}
function taxStatusFromDTO(param) {
    let { taxStatus, taxedAmount } = param;
    if (taxStatus === 'none') return {
        type: 'none'
    };
    if (taxedAmount == null) throw new Error('a taxedAmount tax status arrived without its taxedAmount');
    return {
        type: 'taxedAmount',
        taxedAmount: {
            amountExcludingTax: centsToDecimal(taxedAmount.amountExcludingTax),
            taxAmount: centsToDecimal(taxedAmount.taxAmount),
            amountIncludingTax: centsToDecimal(taxedAmount.amountIncludingTax)
        }
    };
}
function taxStatusToDTO(taxStatus) {
    if (taxStatus.type === 'none') return {
        taxStatus: 'none'
    };
    const { amountExcludingTax, taxAmount, amountIncludingTax } = taxStatus.taxedAmount;
    return {
        taxStatus: 'taxedAmount',
        taxedAmount: {
            amountExcludingTax: dollarsToCents(amountExcludingTax),
            taxAmount: dollarsToCents(taxAmount),
            amountIncludingTax: dollarsToCents(amountIncludingTax)
        }
    };
}
function subLineItemsFromDTO(dto) {
    return dto === null || dto === void 0 ? void 0 : dto.map((subLineItem)=>{
        var _subLineItem_taxable;
        return {
            name: subLineItem.name,
            discount: DiscountModel.fromDTO(subLineItem.discount),
            totalAmount: new Decimal(subLineItem.totalAmount).div(100).toDP(),
            taxable: (_subLineItem_taxable = subLineItem.taxable) !== null && _subLineItem_taxable !== void 0 ? _subLineItem_taxable : false,
            taxStatus: taxStatusFromDTO(subLineItem),
            rate: {
                unit: subLineItem.rate.unit,
                amount: new Decimal(subLineItem.rate.amount).div(100).toDP()
            },
            origin: subLineItem.origin ? {
                type: 'serviceBundle',
                serviceBundle: {
                    bundleItemId: subLineItem.origin.serviceBundle.bundleItemId,
                    serviceTemplateId: subLineItem.origin.serviceBundle.serviceTemplateId
                }
            } : undefined
        };
    });
}
function subLineItemsToDTO(subLineItems) {
    return subLineItems === null || subLineItems === void 0 ? void 0 : subLineItems.map((subLineItem)=>_object_spread_props(_object_spread({
            name: subLineItem.name,
            discount: subLineItem.discount ? subLineItem.discount.toDTO() : null,
            totalAmount: subLineItem.totalAmount.mul(100).toNumber(),
            taxable: subLineItem.taxable
        }, taxStatusToDTO(subLineItem.taxStatus)), {
            rate: {
                unit: subLineItem.rate.unit,
                amount: subLineItem.rate.amount.mul(100).toNumber()
            },
            origin: subLineItem.origin ? {
                type: 'serviceBundle',
                serviceBundle: {
                    bundleItemId: subLineItem.origin.serviceBundle.bundleItemId,
                    serviceTemplateId: subLineItem.origin.serviceBundle.serviceTemplateId
                }
            } : undefined
        }));
}
export function lineItemFromDTO(lineItemDTO) {
    const { taxedAmount } = lineItemDTO, dto = _object_without_properties(lineItemDTO, [
        "taxedAmount"
    ]);
    const taxStatus = taxStatusFromDTO({
        taxStatus: dto.taxStatus,
        taxedAmount
    });
    switch(dto.source){
        case LineItemType.Automatic:
            {
                return _object_spread_props(_object_spread({}, dto), {
                    source: LineItemType.Automatic,
                    taxStatus,
                    totalAmount: new Decimal(dto.totalAmount).div(100).toDP(),
                    rate: _object_spread_props(_object_spread({}, dto.rate), {
                        amount: new Decimal(dto.rate.amount).div(100).toDP()
                    }),
                    automatic: _object_spread_props(_object_spread({}, dto.automatic), {
                        discount: DiscountModel.fromDTO(dto.automatic.discount)
                    }),
                    subLineItems: subLineItemsFromDTO(dto.subLineItems)
                });
            }
        case LineItemType.Manual:
            {
                return _object_spread_props(_object_spread({}, dto), {
                    source: LineItemType.Manual,
                    taxStatus,
                    totalAmount: new Decimal(dto.totalAmount).div(100).toDP(),
                    rate: _object_spread_props(_object_spread({}, dto.rate), {
                        amount: new Decimal(dto.rate.amount).div(100).toDP()
                    }),
                    manual: _object_spread_props(_object_spread({}, dto.manual), {
                        quantity: dto.manual.quantity ? new Decimal(dto.manual.quantity).div(100).toDP() : null,
                        discount: DiscountModel.fromDTO(dto.manual.discount)
                    }),
                    subLineItems: subLineItemsFromDTO(dto.subLineItems)
                });
            }
        case LineItemType.AdHocCharge:
            {
                return _object_spread_props(_object_spread({}, dto), {
                    source: LineItemType.AdHocCharge,
                    taxStatus,
                    totalAmount: new Decimal(dto.totalAmount).div(100).toDP(),
                    rate: _object_spread_props(_object_spread({}, dto.rate), {
                        amount: new Decimal(dto.rate.amount).div(100).toDP()
                    }),
                    adHocCharge: _object_spread_props(_object_spread({}, dto.adHocCharge), {
                        quantity: new Decimal(dto.adHocCharge.quantity).div(100).toDP(),
                        discount: DiscountModel.fromDTO(dto.adHocCharge.discount)
                    })
                });
            }
        case LineItemType.Credit:
            {
                return _object_spread_props(_object_spread({}, dto), {
                    source: LineItemType.Credit,
                    taxStatus,
                    totalAmount: new Decimal(dto.totalAmount).div(100).toDP(),
                    rate: _object_spread_props(_object_spread({}, dto.rate), {
                        amount: new Decimal(dto.rate.amount).div(100).toDP()
                    })
                });
            }
        default:
            {
                throw new Error('unrecognized line item source');
            }
    }
}
export function toDTO(invoiceDraft) {
    var _invoiceDraft_appliedTax;
    const { issueDate, paymentDate, totalAmount } = invoiceDraft;
    const credits = invoiceDraft.credits.map((c)=>c.toJSON());
    const lineItems = invoiceDraft.lineItems.map(lineItemToDTO);
    return {
        attachments: invoiceDraft.attachments,
        issueDate: timeModel.toDateStampObject(issueDate),
        paymentDate: timeModel.toDateStampObject(paymentDate),
        totalAmount: totalAmount.mul(100).toNumber(),
        totals: invoiceDraft.totals ? totalsToDTO(invoiceDraft.totals) : undefined,
        appliedTax: (_invoiceDraft_appliedTax = invoiceDraft.appliedTax) !== null && _invoiceDraft_appliedTax !== void 0 ? _invoiceDraft_appliedTax : undefined,
        credits,
        lineItems
    };
}
export function lineItemToDTO(lineItem) {
    return matchLineItem(lineItem, {
        automatic: (item)=>_object_spread_props(_object_spread({}, item, taxStatusToDTO(item.taxStatus)), {
                source: LineItemType.Automatic,
                totalAmount: item.totalAmount.mul(100).toNumber(),
                rate: _object_spread_props(_object_spread({}, item.rate), {
                    amount: item.rate.amount.mul(100).toNumber()
                }),
                automatic: _object_spread_props(_object_spread({}, item.automatic), {
                    discount: item.automatic.discount && item.automatic.discount.toDTO()
                }),
                subLineItems: subLineItemsToDTO(item.subLineItems)
            }),
        manual: (item)=>_object_spread_props(_object_spread({}, item, taxStatusToDTO(item.taxStatus)), {
                source: LineItemType.Manual,
                totalAmount: item.totalAmount.mul(100).toNumber(),
                rate: _object_spread_props(_object_spread({}, item.rate), {
                    amount: item.rate.amount.mul(100).toNumber()
                }),
                manual: _object_spread_props(_object_spread({}, item.manual), {
                    quantity: item.manual.quantity !== null ? item.manual.quantity.mul(100).toNumber() : null,
                    discount: item.manual.discount && item.manual.discount.toDTO()
                }),
                subLineItems: subLineItemsToDTO(item.subLineItems)
            }),
        adHocCharge: (item)=>_object_spread_props(_object_spread({}, item, taxStatusToDTO(item.taxStatus)), {
                source: LineItemType.AdHocCharge,
                totalAmount: item.totalAmount.mul(100).toNumber(),
                rate: _object_spread_props(_object_spread({}, item.rate), {
                    amount: item.rate.amount.mul(100).toNumber()
                }),
                adHocCharge: _object_spread_props(_object_spread({}, item.adHocCharge), {
                    quantity: item.adHocCharge.quantity.mul(100).toNumber(),
                    discount: item.adHocCharge.discount && item.adHocCharge.discount.toDTO()
                })
            }),
        credit: (item)=>_object_spread_props(_object_spread({}, item, taxStatusToDTO(item.taxStatus)), {
                source: LineItemType.Credit,
                totalAmount: item.totalAmount.mul(100).toNumber(),
                rate: _object_spread_props(_object_spread({}, item.rate), {
                    amount: item.rate.amount.mul(100).toNumber()
                })
            })
    });
}
